import type { CompanyRole } from '../enums/company-role.enum.js'
import { isOneOf } from '../helpers/is-one-of.js'
import type {
  PermissionAction,
  PermissionResource,
  PermissionString,
} from './permission.types.js'

/**
 * Every permission the product grants. The auth user DTO validates against
 * this list, so a new permission has to be added here before a role can use it.
 */
export const ALL_PERMISSIONS = [
  'company.create',
  'company.read',
  'company.update',
  'company.delete',
  'membership.create',
  'membership.read',
  'membership.update',
  'membership.delete',
  'membership.invite',
  'invitation.create',
  'invitation.read',
  'invitation.delete',
  'user.create',
  'user.read',
  'user.update',
  'user.delete',
  'loan.create',
  'loan.read',
  'loan.update',
  'loan.delete',
  'loan.decide',
  'loan_document.read',
  'loan_document.upload',
  'loan_document.delete',
  'loan_program.create',
  'loan_program.read',
  'loan_program.update',
  'loan_program.delete',
  'underwriting_guideline.create',
  'underwriting_guideline.read',
  'underwriting_guideline.update',
  'underwriting_guideline.delete',
] as const satisfies readonly PermissionString[]

/** A permission that appears in {@link ALL_PERMISSIONS}. */
export type CatalogPermission = (typeof ALL_PERMISSIONS)[number]

type ActionOf<P, R extends string> = P extends `${R}.${infer A extends PermissionAction}`
  ? A
  : never

/**
 * Actions the catalog grants on resource `R`. `CatalogAction<'loan'>` excludes
 * `invite`, so a `resource, action` pair that no role can hold fails to compile.
 */
export type CatalogAction<R extends PermissionResource> = ActionOf<CatalogPermission, R>

/** Narrows a `resource.action` string to a member of {@link ALL_PERMISSIONS}. */
export function isCatalogPermission(value: string): value is CatalogPermission {
  return isOneOf(value, ALL_PERMISSIONS)
}

/**
 * Fixed role → permission grants for company staff.
 * Platform admins bypass this table (all permissions).
 * Borrowers resolve grants via loan participation, not this map.
 *
 * Loan field-level limits: see `loan-field-rules.ts`. Both share `loan.update`;
 * admin-only fields are enforced in the loan service. Decision statuses
 * (`LOAN_DECISION_STATUSES`) also need `loan.decide` on the loan's lender.
 */
export const ROLE_PERMISSIONS = {
    LENDER_USER: [
      'company.read',
      'membership.read',
      'membership.invite',
      'invitation.create',
      'invitation.read',
      'loan.create',
      'loan.read',
      'loan.update',
      // Credit decisions belong to the lender (docs/rbac.md). Checked against the
      // loan's lender / table funding lender membership, not any company.
      'loan.decide',
      'loan_document.read',
      'loan_document.upload',
      'loan_program.create',
      'loan_program.read',
      'loan_program.update',
      'loan_program.delete',
      'underwriting_guideline.create',
      'underwriting_guideline.read',
      'underwriting_guideline.update',
      'underwriting_guideline.delete',
    ],
    ORIGINATOR_RMLO: [
      'company.read',
      'membership.read',
      'membership.invite',
      'invitation.create',
      'invitation.read',
      'loan.create',
      'loan.read',
      'loan.update',
      'loan_document.read',
      'loan_document.upload',
      // Read lender product catalog when dual-cap or shared context; no write.
      'loan_program.read',
      'underwriting_guideline.read',
    ],
    ORIGINATOR_CLERICAL: [
      'company.read',
      'membership.read',
      'membership.invite',
      'invitation.create',
      'invitation.read',
      'loan.create',
      'loan.read',
      'loan.update',
      'loan_document.read',
      'loan_document.upload',
      'loan_program.read',
      'underwriting_guideline.read',
    ],
  } as const satisfies Record<CompanyRole, readonly CatalogPermission[]>

/** Borrower grants are always loan-scoped at the data layer. */
export const BORROWER_PERMISSIONS = [
  'loan.read',
  'loan_document.read',
  'loan_document.upload',
] as const satisfies readonly CatalogPermission[]

/**
 * Whether `role`'s fixed grants include `permission`. Use for membership-scoped
 * checks that must ignore the session's current company.
 */
export function roleHasPermission(
  role: CompanyRole,
  permission: CatalogPermission,
): boolean {
  const grants: readonly CatalogPermission[] = ROLE_PERMISSIONS[role]
  return grants.includes(permission)
}
